// Stale-if-error read cache for the widget.
//
// WHY IT LIVES HERE AND NOT IN A GATEWAY. On 2026-08-31 dev Postgres was down
// for ~9.5h and readers of the embedded widget saw an error. The obvious fix —
// cache the read paths at the edge — does not actually work for this:
//
//   * Traefik CE has no HTTP cache middleware at all (that is Enterprise).
//   * Kong's proxy-cache does not serve stale content when the upstream is
//     failing; it just misses and passes the error through.
//   * Even with stale-if-error, a TTL long enough to span nine hours would be
//     serving stale articles all day in normal operation.
//
// The only layer that can make a nine-hour database outage invisible to a
// reader is the reader's own browser. So: every successful read is written
// here, and a failed read falls back to it rather than throwing. Content the
// reader has already seen stays available; the panel says plainly that it is
// showing a saved copy, and from when.
//
// Best-effort throughout. localStorage can throw outright (Safari private mode,
// host pages served with storage blocked) and can be full, and a cache miss is
// never worth surfacing as an error — the network path's own message is.
import { viewerId } from "./viewer";
import {
  getArticle,
  listArticles,
  type ArticleFull,
  type ArticleListItem,
  type WidgetConfig,
} from "./data";

const NS = "skycell-kb-cache";
// Bump to invalidate every cached entry after a shape change.
const VERSION = "v1";
// A single article body larger than this is not worth the quota it would cost;
// localStorage is ~5 MB for the WHOLE origin, which we share with the host page.
const MAX_BODY_BYTES = 512 * 1024;

type Entry<T> = { savedAt: number; data: T };

/** What a cached read produced. `stale` means the network failed and this came
 *  from storage; `savedAt` is when it was last successfully fetched. */
export type Cached<T> = { data: T; stale: boolean; savedAt: number | null };

/**
 * Scope key for this embed. Three things must not share a cache: different apps
 * (different content), different share tokens (a rotated or revoked token must
 * not keep serving what the old one unlocked), and different READERS — the list
 * is role-filtered now, so one user's cache must not be served to the next
 * person to sign in on the same machine. The token is hashed rather than stored
 * — it is a credential, and nothing here needs to read it back.
 */
function scopeOf(cfg: WidgetConfig): string {
  const raw = `${cfg.kbUrl}|${cfg.app}|${cfg.token ?? ""}|${viewerId() ?? ""}`;
  // djb2. Not cryptographic — this only has to separate scopes, not hide them.
  let h = 5381;
  for (let i = 0; i < raw.length; i++) h = ((h << 5) + h + raw.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

const listKey = (cfg: WidgetConfig) => `${NS}:${VERSION}:${scopeOf(cfg)}:list`;
const articleKey = (cfg: WidgetConfig, slug: string) =>
  `${NS}:${VERSION}:${scopeOf(cfg)}:a:${slug}`;

function read<T>(key: string): Entry<T> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Entry<T>;
    // Written by an older/newer build, or hand-edited. Treat as a miss.
    if (!parsed || typeof parsed.savedAt !== "number" || !("data" in parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Drop this scope's article bodies, keeping the list — the list is small and
 *  the most valuable thing to still have when the network is down. */
function evictBodies(cfg: WidgetConfig): void {
  const prefix = `${NS}:${VERSION}:${scopeOf(cfg)}:a:`;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) doomed.push(k);
    }
    for (const k of doomed) localStorage.removeItem(k);
  } catch {
    /* nothing we can do, and nothing worth telling the reader */
  }
}

function write<T>(cfg: WidgetConfig, key: string, data: T): void {
  const payload = JSON.stringify({ savedAt: Date.now(), data } satisfies Entry<T>);
  try {
    localStorage.setItem(key, payload);
  } catch {
    // Almost always QuotaExceededError. Make room by dropping article bodies
    // and try once more; if it still fails, this browser simply has no cache.
    evictBodies(cfg);
    try {
      localStorage.setItem(key, payload);
    } catch {
      /* give up quietly — the cache is an optimisation, not a requirement */
    }
  }
}

/**
 * The article list, from the network when it works and from storage when it
 * does not. Rethrows only when there is nothing cached to fall back to, so the
 * reader still gets the network path's own message on a cold first load.
 */
export async function loadArticles(cfg: WidgetConfig): Promise<Cached<ArticleListItem[]>> {
  try {
    const items = await listArticles(cfg);
    write(cfg, listKey(cfg), items);
    return { data: items, stale: false, savedAt: Date.now() };
  } catch (err) {
    const hit = read<ArticleListItem[]>(listKey(cfg));
    if (hit && Array.isArray(hit.data) && hit.data.length > 0) {
      return { data: hit.data, stale: true, savedAt: hit.savedAt };
    }
    throw err;
  }
}

/**
 * One article, same contract. A successful fetch that finds nothing is NOT a
 * failure — it means the article is genuinely gone or out of scope, so it is
 * reported as such rather than papered over with a cached copy.
 */
export async function loadArticle(
  cfg: WidgetConfig,
  slug: string,
): Promise<Cached<ArticleFull | null>> {
  try {
    const article = await getArticle(cfg, slug);
    if (article) {
      // Oversized bodies are skipped rather than evicting everything else.
      if (article.content.length <= MAX_BODY_BYTES) {
        write(cfg, articleKey(cfg, slug), article);
      }
    }
    return { data: article, stale: false, savedAt: Date.now() };
  } catch (err) {
    const hit = read<ArticleFull>(articleKey(cfg, slug));
    if (hit && hit.data) return { data: hit.data, stale: true, savedAt: hit.savedAt };
    throw err;
  }
}

/** "3 minutes ago" / "yesterday", for the saved-copy notice. Deliberately
 *  coarse: the reader needs to know roughly how old this is, not to the second. */
export function savedAgo(savedAt: number, now = Date.now()): string {
  const mins = Math.max(0, Math.round((now - savedAt) / 60000));
  if (mins < 1) return "just now";
  if (mins === 1) return "1 minute ago";
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours === 1) return "1 hour ago";
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}
